export type BrandingImage = 'logo' | 'favicon' | 'background'

export interface PreparedImage {
  // Base64 without the data: prefix, which is what the endpoint's bytes field expects.
  content: string
  // Same image as a data url, for the thumbnail and the portal preview before it is saved.
  previewUrl: string
  bytes: number
}

// Matches MaxImageBytes in the settings-api branding domain. The endpoint refuses more, and
// Envoy's transcoder refuses a body over 1 MiB, which base64 reaches from about 780 KiB.
export const MAX_IMAGE_BYTES = 512 * 1024

// What the picker accepts. SVG is absent on purpose: it can carry script and these images are
// served to shoppers, so the endpoint refuses it and the picker says so rather than letting a
// merchant discover it as a failed upload.
export const ACCEPTED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp'
] as const

// Per image, the largest useful size and the format to store it in. A logo renders about 32px
// tall and a favicon 16-32px, so these are already generous for a retina screen; the background
// is the only one a shopper downloads at page size, which is why it is re-encoded as JPEG.
const RECIPES: Record<
  BrandingImage,
  { maxSide: number; mime: string; quality?: number }
> = {
  logo: { maxSide: 600, mime: 'image/png' },
  favicon: { maxSide: 256, mime: 'image/png' },
  background: { maxSide: 2400, mime: 'image/jpeg', quality: 0.82 }
}

// How far to keep shrinking when the first encode still exceeds the budget. PNG has no quality
// knob, so dimensions are the only lever, and a bound stops a pathological image looping.
const SHRINK_FACTOR = 0.75
const SHRINK_ATTEMPTS = 6

export class ImageRejected extends Error {
  constructor(readonly reason: 'type' | 'unreadable' | 'too-large') {
    super(reason)
    this.name = 'ImageRejected'
  }
}

// prepareImage fits a merchant's file to what the endpoint accepts instead of asking them to do
// it: the file is decoded, drawn down to the recipe's bounds and re-encoded, shrinking until it
// is inside the byte budget.
export async function prepareImage(
  file: File,
  image: BrandingImage
): Promise<PreparedImage> {
  if (!isAcceptedImageType(file.type)) throw new ImageRejected('type')

  const recipe = RECIPES[image]
  const bitmap = await decode(file)

  try {
    for (let attempt = 0; attempt <= SHRINK_ATTEMPTS; attempt++) {
      const maxSide = recipe.maxSide * SHRINK_FACTOR ** attempt
      const size = fitWithin(bitmap.width, bitmap.height, maxSide)
      const previewUrl = encode(bitmap, size, recipe.mime, recipe.quality)
      const content = base64Of(previewUrl)
      const bytes = base64Bytes(content)

      if (bytes <= MAX_IMAGE_BYTES) return { content, previewUrl, bytes }
    }
  } finally {
    bitmap.close()
  }

  throw new ImageRejected('too-large')
}

export function isAcceptedImageType(type: string): boolean {
  return (ACCEPTED_IMAGE_TYPES as readonly string[]).includes(type)
}

// fitWithin scales the dimensions so neither side passes the bound, keeping the aspect ratio and
// never enlarging: upscaling a small logo would only cost bytes without adding detail.
export function fitWithin(
  width: number,
  height: number,
  maxSide: number
): { width: number; height: number } {
  const scale = Math.min(1, maxSide / Math.max(width, height))
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale))
  }
}

export function base64Of(dataUrl: string): string {
  const comma = dataUrl.indexOf(',')
  return comma === -1 ? dataUrl : dataUrl.slice(comma + 1)
}

// The decoded length of base64, which is what the endpoint measures — the encoded string is a
// third longer, so checking its length would refuse images that are actually within budget.
export function base64Bytes(base64: string): number {
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0
  return Math.floor((base64.length * 3) / 4) - padding
}

async function decode(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file)
  } catch {
    // A file whose extension says image but whose bytes do not; the endpoint would refuse it too.
    throw new ImageRejected('unreadable')
  }
}

function encode(
  bitmap: ImageBitmap,
  size: { width: number; height: number },
  mime: string,
  quality?: number
): string {
  const canvas = document.createElement('canvas')
  canvas.width = size.width
  canvas.height = size.height

  const context = canvas.getContext('2d')
  if (!context) throw new ImageRejected('unreadable')
  context.drawImage(bitmap, 0, 0, size.width, size.height)

  return canvas.toDataURL(mime, quality)
}
